import { createFileRoute } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import {
  LegalList,
  LegalPage,
  LegalParagraph,
  LegalSection,
  LegalSubheading,
  LegalTerm,
} from '@/components/legal/LegalPage';

export const Route = createFileRoute('/data-deletion')({
  component: DataDeletion,
});

function DataDeletion() {
  return (
    <LegalPage icon={Trash2} title="Data Deletion Policy" lastUpdated="25 June 2026">
      <section className="space-y-3">
      <LegalParagraph>{"This Data Deletion Policy explains how Future Builder Co Pty Ltd (ABN 87 670 515 796) (“we”, “us”, “our”) handles the deletion of user data on the Alfie platform (“Alfie”, “the Platform”) accessible at app.alfie.co."}</LegalParagraph>
      <LegalParagraph>{"This policy forms part of our commitment to your privacy under the Australian Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). Please read this policy alongside our Privacy Policy at https://app.alfie.co/privacy."}</LegalParagraph>
      </section>
      <LegalSection heading="1. Your Right to Data Deletion">
        <LegalParagraph>{"You have the right to request the deletion of your personal information held by us. This includes:"}</LegalParagraph>
        <LegalList>
          <li>{"Account information (name, email address, business details)."}</li>
          <li>{"Business data you have entered into or connected with Alfie."}</li>
          <li>{"AI-generated insights and reports derived from your data."}</li>
          <li>{"Usage data and analytics associated with your account."}</li>
          <li>{"Communications and support history."}</li>
        </LegalList>
      </LegalSection>
      <LegalSection heading="2. How to Request Data Deletion">
        <LegalSubheading>{"2.1 Self-Service Account Deletion"}</LegalSubheading>
        <LegalParagraph>{"You can initiate account and data deletion directly through the Platform:"}</LegalParagraph>
        <LegalList>
          <li>{"Log in to your Alfie account at app.alfie.co."}</li>
          <li>{"Navigate to "}<LegalTerm>{"Settings > Account > Delete Account"}</LegalTerm>{"."}</li>
          <li>{"Confirm your identity and deletion request."}</li>
          <li>{"Your account will be deactivated immediately and data deletion will commence."}</li>
        </LegalList>
        <LegalSubheading>{"2.2 Email Request"}</LegalSubheading>
        <LegalParagraph>{"You can also request data deletion by emailing us at "}<LegalTerm>{"support@alfie.co"}</LegalTerm>{" with:"}</LegalParagraph>
        <LegalList>
          <li>{"The subject line “Data Deletion Request”."}</li>
          <li>{"Your full name and the email address associated with your Alfie account."}</li>
          <li>{"Whether you are requesting full account deletion or deletion of specific data only."}</li>
        </LegalList>
        <LegalParagraph>{"We will acknowledge your request within 5 business days and complete the deletion within 30 days."}</LegalParagraph>
        <LegalSubheading>{"2.3 Authorised Representative"}</LegalSubheading>
        <LegalParagraph>{"If you are unable to submit a request yourself, an authorised representative may submit one on your behalf with appropriate verification of authority."}</LegalParagraph>
      </LegalSection>
      <LegalSection heading="3. What Happens When You Request Deletion">
        <LegalSubheading>{"3.1 Immediate Actions (Within 24 Hours)"}</LegalSubheading>
        <LegalList>
          <li>{"Your account is deactivated and you will no longer be able to log in."}</li>
          <li>{"Your data is removed from active Platform systems and is no longer accessible."}</li>
          <li>{"All active third-party integrations (Xero, GoHighLevel, Google, etc.) are disconnected."}</li>
          <li>{"Any active subscription is cancelled in accordance with the Terms and Conditions."}</li>
        </LegalList>
        <LegalSubheading>{"3.2 Data Deletion Timeline"}</LegalSubheading>
        <LegalParagraph>{"Data Type"}</LegalParagraph>
        <LegalParagraph>{"Deletion Timeline"}</LegalParagraph>
        <LegalParagraph>{"Account profile and settings"}</LegalParagraph>
        <LegalParagraph>{"Within 30 days of request"}</LegalParagraph>
        <LegalParagraph>{"Business data and AI insights"}</LegalParagraph>
        <LegalParagraph>{"Within 30 days of request"}</LegalParagraph>
        <LegalParagraph>{"Connected integration data"}</LegalParagraph>
        <LegalParagraph>{"Within 30 days of request"}</LegalParagraph>
        <LegalParagraph>{"Usage logs and analytics"}</LegalParagraph>
        <LegalParagraph>{"Within 30 days of request"}</LegalParagraph>
        <LegalParagraph>{"Communications and support tickets"}</LegalParagraph>
        <LegalParagraph>{"Within 30 days of request"}</LegalParagraph>
        <LegalParagraph>{"Encrypted backups"}</LegalParagraph>
        <LegalParagraph>{"Within 60 days of request"}</LegalParagraph>
        <LegalParagraph>{"Billing and transaction records"}</LegalParagraph>
        <LegalParagraph>{"Retained for up to 7 years (legal obligation)"}</LegalParagraph>
        <LegalSubheading>{"3.3 Grace Period"}</LegalSubheading>
        <LegalParagraph>{"After you request deletion, there is a "}<LegalTerm>{"14-day grace period"}</LegalTerm>{" during which you can contact us at support@alfie.co to cancel your deletion request and restore your account. After this grace period, deletion is irreversible."}</LegalParagraph>
      </LegalSection>
      <LegalSection heading="4. Data We Are Required to Retain">
        <LegalParagraph>{"Certain data may be retained beyond your deletion request where we are legally required to do so:"}</LegalParagraph>
        <LegalList>
          <li><LegalTerm>{"Financial Records:"}</LegalTerm>{" Billing records, invoices and transaction history may be retained for up to 7 years to comply with Australian tax law and the Corporations Act 2001 (Cth)."}</li>
          <li><LegalTerm>{"Legal Holds:"}</LegalTerm>{" If your data is subject to a legal hold, litigation or regulatory investigation, we may be required to retain relevant data until the matter is resolved."}</li>
          <li><LegalTerm>{"Fraud Prevention:"}</LegalTerm>{" Limited data may be retained to prevent fraud or enforce our terms, such as records of terminated accounts."}</li>
        </LegalList>
        <LegalParagraph>{"Where data is retained for legal purposes, it is stored securely, access is restricted, and it is deleted as soon as the retention obligation expires."}</LegalParagraph>
      </LegalSection>
      <LegalSection heading="5. Aggregated and De-identified Data">
        <LegalParagraph>{"Data that has been fully de-identified and aggregated so that it cannot reasonably be used to identify you is not considered personal information under the Privacy Act 1988. Such data may be retained for analytical and service improvement purposes."}</LegalParagraph>
      </LegalSection>
      <LegalSection heading="6. Third-Party Data">
        <LegalSubheading>{"6.1 Data Received from Integrations"}</LegalSubheading>
        <LegalParagraph>{"When you delete your Alfie account, all data received from connected third-party services (such as Xero, GoHighLevel and Google) is deleted from our systems in accordance with the timelines above. Deletion from Alfie does not affect the data held by those third-party services — you should contact them directly regarding their data deletion processes."}</LegalParagraph>
        <LegalSubheading>{"6.2 Data Shared with Service Providers"}</LegalSubheading>
        <LegalParagraph>{"We will instruct our service providers to delete your personal information from their systems. Deletion timelines may vary by provider but will generally be completed within 90 days."}</LegalParagraph>
      </LegalSection>
      <LegalSection heading="7. White Label and Reseller Accounts">
        <LegalParagraph>{"If you access Alfie through a white-label or reseller partner:"}</LegalParagraph>
        <LegalList>
          <li>{"Submitting a deletion request to us will delete your data from Future Builder Co’s systems."}</li>
          <li>{"You should also contact the relevant partner directly regarding any data they hold independently."}</li>
          <li>{"We will notify the partner of your deletion request where required."}</li>
        </LegalList>
      </LegalSection>
      <LegalSection heading="8. Data Export Before Deletion">
        <LegalParagraph>{"We recommend exporting your data before requesting deletion. You can export your data through:"}</LegalParagraph>
        <LegalList>
          <li><LegalTerm>{"In-Platform Export:"}</LegalTerm>{" Navigate to Settings > Data > Export to download your data in CSV, PDF or JSON format."}</li>
          <li><LegalTerm>{"Email Request:"}</LegalTerm>{" Contact support@alfie.co to request a full data export."}</li>
        </LegalList>
        <LegalParagraph>{"Once deletion is complete, we cannot recover your data."}</LegalParagraph>
      </LegalSection>
      <LegalSection heading="9. Confirmation of Deletion">
        <LegalParagraph>{"Once your data has been deleted, we will send a confirmation email to the email address associated with your account (or the address provided in your deletion request). This confirmation will include:"}</LegalParagraph>
        <LegalList>
          <li>{"The date deletion was completed."}</li>
          <li>{"A summary of the categories of data deleted."}</li>
          <li>{"Details of any data retained for legal reasons and the applicable retention period."}</li>
        </LegalList>
      </LegalSection>
      <LegalSection heading="10. Complaints">
        <LegalParagraph>{"If you are not satisfied with how we have handled your data deletion request, you may:"}</LegalParagraph>
        <LegalList>
          <li>{"Contact us at support@alfie.co to escalate your concern."}</li>
          <li>{"Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at www.oaic.gov.au."}</li>
        </LegalList>
      </LegalSection>
      <LegalSection heading="11. Contact Us">
        <LegalParagraph>{"For questions about this Data Deletion Policy or to submit a data deletion request:"}</LegalParagraph>
        <LegalParagraph><LegalTerm>{"Future Builder Co Pty Ltd"}</LegalTerm>{" ABN 87 670 515 796"}</LegalParagraph>
        <LegalParagraph>{"Email: support@alfie.co Website: https://app.alfie.co"}</LegalParagraph>
        <LegalParagraph>{"This Data Deletion Policy is governed by the laws of Tasmania, Australia."}</LegalParagraph>
      </LegalSection>
    </LegalPage>
  );
}
